import Link, { LinkProps } from "next/link";
import { useRouter } from "next/router";
import {
  AnchorHTMLAttributes,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
} from "react";
import type { UrlObject } from "url";
import { useNearViewport } from "../../../hooks/useNearViewport";
import { afterInteractionOrDelay } from "../../../utils/afterPageLoad";

// When to prefetch the linked page's code and data.
// - "disable": never prefetch automatically
// - "scroll": after the first window scroll
// - "inView": once the link enters the viewport
// Links with target="_blank" render a plain <a> (no prefetch at all, including Next's
// hover prefetch): the new tab can't use prefetched data.
// Links to the current path (only query/hash differ, e.g. ?tags= filters) are always
// "disable": the page's code and data are already loaded.
export type PrefetchMode = "disable" | "scroll" | "inView";

type OptimizedLinkProps = Omit<LinkProps, "prefetch"> &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, keyof LinkProps> & {
    prefetchMode?: PrefetchMode;
    children?: ReactNode;
  };

function hrefToString(href: LinkProps["href"]): string {
  if (typeof href === "string") return href;
  const { pathname = "", query, hash } = href as UrlObject;
  const search =
    query && typeof query === "object"
      ? new URLSearchParams(
          Object.entries(query).flatMap(([key, value]) =>
            (Array.isArray(value) ? value : [value])
              .filter((v) => v !== undefined && v !== null)
              .map((v) => [key, String(v)]),
          ),
        ).toString()
      : (query ?? "");
  return `${pathname}${search ? `?${search}` : ""}${hash ?? ""}`;
}

// True when `href` points at the current page's path, ignoring query, hash and a
// trailing slash. Relative hrefs (e.g. "?tags=…") resolve against the current path.
function isSamePath(href: string, currentPath: string) {
  const base = new URL(currentPath, "http://localhost");
  const target = new URL(href, base);
  const normalize = (pathname: string) => pathname.replace(/\/+$/, "") || "/";
  return (
    target.origin === base.origin &&
    normalize(target.pathname) === normalize(base.pathname)
  );
}

// Resolves on the visitor's first interaction, or INTERACTION_FALLBACK_DELAY_MS after
// the load event; shared by every link.
let pageSettled: Promise<void> | undefined;
function whenPageSettled() {
  pageSettled ??= new Promise((resolve) => {
    afterInteractionOrDelay(resolve);
  });
  return pageSettled;
}

// Runs once the page has settled and the browser is idle, so prefetching doesn't
// compete with the page's own loading. Idle alone wasn't enough: on fast devices
// the browser is idle before LCP, and the prefetches delayed it. A fixed delay
// wasn't either: on pages with many links the burst of prefetches kept Lighthouse
// measuring (see INTERACTION_FALLBACK_DELAY_MS).
function whenIdle(callback: () => void) {
  let cancelled = false;
  let cancelIdle: (() => void) | undefined;
  whenPageSettled().then(() => {
    if (cancelled) return;
    if (typeof window.requestIdleCallback === "function") {
      const id = window.requestIdleCallback(callback, { timeout: 2000 });
      cancelIdle = () => window.cancelIdleCallback(id);
    } else {
      const id = window.setTimeout(callback, 200);
      cancelIdle = () => window.clearTimeout(id);
    }
  });
  return () => {
    cancelled = true;
    cancelIdle?.();
  };
}

// next/link with Next's automatic viewport prefetch replaced by a deferred one.
// Hover prefetch (Next's default with prefetch={false}) still applies in every mode.
export default function OptimizedLink({
  prefetchMode: prefetchModeProp = "inView",
  href,
  children,
  ...props
}: OptimizedLinkProps) {
  const router = useRouter();
  const ref = useRef<HTMLAnchorElement>(null);
  const inView = useNearViewport(ref, "200px");
  const hrefString = hrefToString(href);
  const prefetchMode: PrefetchMode =
    props.target === "_blank" || isSamePath(hrefString, router.asPath)
      ? "disable"
      : prefetchModeProp;

  const prefetch = useCallback(() => {
    // Prefetch failures only lose the speed-up; navigation still works.
    router.prefetch(hrefString).catch(() => {});
  }, [router, hrefString]);

  useEffect(() => {
    if (prefetchMode !== "scroll") return;

    let cancelIdle: (() => void) | undefined;
    const onScroll = () => {
      cancelIdle = whenIdle(prefetch);
    };
    window.addEventListener("scroll", onScroll, { once: true, passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      cancelIdle?.();
    };
  }, [prefetchMode, prefetch]);

  useEffect(() => {
    if (prefetchMode !== "inView" || !inView) return;
    return whenIdle(prefetch);
  }, [prefetchMode, inView, prefetch]);

  if (props.target === "_blank") {
    // Drop next/link-only props so they don't end up as DOM attributes.
    const {
      as: _as,
      replace: _replace,
      scroll: _scroll,
      shallow: _shallow,
      passHref: _passHref,
      locale: _locale,
      legacyBehavior: _legacyBehavior,
      onNavigate: _onNavigate,
      ...anchorProps
    } = props;
    return (
      <a ref={ref} href={hrefString} {...anchorProps}>
        {children}
      </a>
    );
  }

  return (
    <Link ref={ref} href={href} prefetch={false} {...props}>
      {children}
    </Link>
  );
}
